import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Home, ArrowLeft, Code } from "lucide-react";

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <div className="min-h-screen bg-site flex items-center justify-center relative overflow-hidden">
      {/* Subtle spotlight overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-20" style={{ backgroundImage: 'var(--gradient-spotlight)' }} />
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          variants={container}
          initial="hidden"
          animate="show"
          className="text-center max-w-2xl mx-auto"
        >
          {/* 404 Code Icon */}
          <motion.div variants={item} className="mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Code className="w-12 h-12 text-primary" />
            </div>
          </motion.div>

          {/* 404 Title */}
          <motion.h1 
            variants={item}
            className="font-heading text-6xl sm:text-7xl md:text-8xl font-bold text-foreground mb-4"
          >
            <span className="gradient-text">404</span>
          </motion.h1>

          {/* Error Message */}
          <motion.h2 
            variants={item}
            className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-4"
          >
            Oops! Page not found
          </motion.h2>

          {/* Description */}
          <motion.p 
            variants={item}
            className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg mx-auto"
          >
            The page you're looking for seems to have vanished into the digital void. 
            Let's get you back to coding adventures!
          </motion.p>

          {/* Action Buttons */}
          <motion.div 
            variants={item}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button 
              variant="hero" 
              size="xl" 
              className="group px-8 py-4 text-lg font-bold"
              onClick={() => navigate('/')}
            >
              <Home className="mr-2 h-4 w-4" />
              Return to Home
            </Button>
            
            <Button 
              variant="outline" 
              size="xl"
              className="px-8 py-4 text-lg font-medium border-primary text-primary hover:bg-primary/10 hover:border-primary transition-colors"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Back
            </Button>
          </motion.div>

          {/* Fun coding message */}
          <motion.div 
            variants={item}
            className="mt-12 p-4 rounded-lg bg-card/50 border border-border/30 backdrop-blur-sm"
          >
            <p className="text-sm text-muted-foreground font-mono">
              &gt; Error: Page "{location.pathname}" not found in directory<br/>
              &gt; Hint: Try checking the URL or return to homepage<br/>
              &gt; Status: Ready to code again 🐍
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFound;
